/** Pure helpers behind the review queue's AI stem rewrites (STORY-045).
 *
 * Kept free of value imports so Node's own test runner can load them (see `usage.ts`). */
import type { Item, Rewrite, RewriteIn } from "../api/types";
import type { ItemStat } from "./api";

/** Item types whose answers are option keys; any other type's answers may be typed text. */
const OPTION_TYPES = new Set(["single", "multi", "hotspot"]);

/** The request for one queue entry. The flags come from the statistic; its answer shares only
 * for option items, so a short_text answer a student typed never leaves the browser. Typed as
 * the generated `RewriteIn`, so `tsc` checks the hand-written `ItemFlag` codes against the
 * contract's. The server filters again against the stored item. */
export function rewriteRequest(stat: Pick<ItemStat, "flags" | "answer_shares" | "item_type">): RewriteIn {
  return {
    flags: stat.flags.map((f) => ({ code: f.code, p: f.p ?? null, discrimination: f.discrimination ?? null, keys: f.keys ?? null })),
    answer_shares: stat.item_type && OPTION_TYPES.has(stat.item_type) ? { ...stat.answer_shares } : {},
  };
}

export type RewriteState = "none" | "working" | "ready" | "failed" | "archived";

/** What the queue entry shows for its latest rewrite. */
export function rewriteState(r: Pick<Rewrite, "status" | "draft"> | null | undefined): RewriteState {
  if (!r) return "none";
  if (r.status === "queued" || r.status === "running") return "working";
  if (r.status === "failed") return "failed";
  return r.draft ? "ready" : "archived";
}

const KNOWN = new Set(["no_objective", "stem_too_long", "unchanged_stem", "ai_unavailable", "ai_failed"]);

/** The message key for a failure: a sentence of its own for each known code. */
export function rewriteErrorKey(error: string | null | undefined): string {
  return error && KNOWN.has(error) ? `items.rewrite.errors.${error}` : "items.rewrite.errors.other";
}

/** The queries an item write can change: the bank, the course, and the review queue's
 * rewrites, whose `draft` is the draft item as it was when the list was read. Archiving,
 * saving, approving or duplicating the draft leaves that copy stale otherwise. */
export function itemQueryKeys(courseId: string): string[][] {
  return [["items", courseId], ["course", courseId], ["rewrites", courseId]];
}

/** The draft as it is now, for the editor: fetched by id, never the copy cached with the
 * rewrite, which an earlier save may have overtaken. `null` when it is gone (archived). */
export async function currentDraft(
  r: Pick<Rewrite, "draft">,
  getItem: (itemId: string) => Promise<Item>,
  isGone: (e: unknown) => boolean,
): Promise<Item | null> {
  if (!r.draft) return null;
  try {
    return await getItem(r.draft.item_id);
  } catch (e) {
    if (isGone(e)) return null;
    throw e;
  }
}
